import React, { useState, useEffect } from 'react';
import { FiPlus, FiRefreshCw } from 'react-icons/fi';
import {
  getTasks,
  pauseTask,
  resumeTask,
  extendDeadline,
  endTaskEarly
} from '../../../api/retailPerformanceTasks';
import { useAuth } from '../../../contexts/AuthContext';
import PerformanceTaskCard from '../../../components/retail/performance-tasks/PerformanceTaskCard';
import CreateTaskModal from '../../../components/retail/performance-tasks/CreateTaskModal';
import { toast } from 'react-toastify';
import LoadingSpinner from '../../../components/common/LoadingSpinner';

const ActiveTasksPage = () => {
  const { isAdmin } = useAuth();
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [filter, setFilter] = useState('active');

  useEffect(() => {
    fetchTasks();
    const interval = setInterval(fetchTasks, 30000);
    return () => clearInterval(interval);
  }, [filter]);

  const fetchTasks = async () => {
    try {
      const response = await getTasks({
        status: filter === 'all' ? ['active', 'paused', 'scheduled'] : [filter]
      });
      if (response.success) setTasks(response.data || []);
    } catch (error) {
      console.error('Error fetching tasks:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAction = async (action, task) => {
    try {
      let response;
      switch (action) {
        case 'pause':
          response = await pauseTask(task.id);
          break;
        case 'resume':
          response = await resumeTask(task.id);
          break;
        default:
          return;
      }
      if (response.success) {
        toast.success(`Task ${action}d successfully`);
        fetchTasks();
      }
    } catch (error) {
      toast.error(error.response?.data?.message || `Failed to ${action} task`);
    }
  };

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
            Active Performance Tasks
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mt-1">Monitor ongoing performance tasks</p>
        </div>
        <div className="flex gap-3">
          <button
            onClick={fetchTasks}
            className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            <FiRefreshCw /> Refresh
          </button>
          {isAdmin && (
            <button
              onClick={() => setShowCreateModal(true)}
              className="flex items-center gap-2 px-4 py-2 bg-accent-gold text-white rounded-lg hover:bg-accent-gold-dark"
            >
              <FiPlus /> Create Task
            </button>
          )}
        </div>
      </div>

      <div className="mb-6 flex gap-2">
        {['active', 'paused', 'scheduled', 'all'].map((status) => (
          <button
            key={status}
            onClick={() => setFilter(status)}
            className={`px-4 py-2 rounded-lg ${filter === status ? 'bg-accent-gold text-white' : 'bg-gray-200 text-gray-700'}`}
          >
            {status.charAt(0).toUpperCase() + status.slice(1)}
          </button>
        ))}
      </div>

      {loading ? (
        <LoadingSpinner message="Loading active tasks..." className="py-12" />
      ) : tasks.length === 0 ? (
        <div className="text-center py-12 bg-white dark:bg-gray-800 rounded-lg">
          <p className="text-gray-500">No {filter} tasks found</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {tasks.map((task) => (
            <PerformanceTaskCard
              key={task.id}
              task={task}
              showActions={isAdmin}
              onPause={(t) => handleAction('pause', t)}
              onResume={(t) => handleAction('resume', t)}
            />
          ))}
        </div>
      )}

      <CreateTaskModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onSuccess={() => {
          setShowCreateModal(false);
          fetchTasks();
        }}
      />
    </div>
  );
};

export default ActiveTasksPage;
